/**
 * Web component for video playback inside app-rendered HTML.
 *
 * Usage:
 *   <anear-video src="https://assets.anear.me/.../clip.mp4"
 *                poster="https://assets.anear.me/.../poster.jpg"
 *                title="First cut"
 *                artist="Golden Hour"
 *                controls></anear-video>
 *
 *   Transcoded asset (preferred): supply HLS + MP4 and a status.
 *   <anear-video hls="https://assets.anear.me/.../hls/index.m3u8"
 *                mp4="https://assets.anear.me/.../mp4/delivery.mp4"
 *                poster="https://assets.anear.me/.../poster.0000000.jpg"
 *                status="ready" controls></anear-video>
 *
 * Thin wrapper around a native <video> that keeps mobile-friendly defaults
 * (playsinline). App-agnostic: it knows only video sources + a generic transcode
 * `status` ("processing" | "ready"); it has no concept of any app's domain.
 *
 * Optional `title` and `artist` become the page title and Media Session metadata
 * while this player is the active one, so AirPlay can show the film instead of
 * the site title. The component does not know which app it is playing.
 *
 * Source selection: when `hls` is provided and the browser plays HLS natively
 * (Safari/iOS), HLS is used; otherwise the component falls back to `mp4`/`src`.
 * While `status="processing"` the poster is shown with a processing overlay and
 * no source is loaded; once `status="ready"` (or absent) playback is enabled.
 */
import { clearDocumentTitleLayer, setDocumentTitleLayer } from '../Utils/documentTitle'

const HLS_MIME = 'application/vnd.apple.mpegurl'
const registeredPlayers = []
let activePlayer = null

const attrText = (el, name) => (el.getAttribute(name) || '').trim()

const httpPoster = (el) => {
  const poster = el.getAttribute('poster') || ''
  return /^https?:\/\//i.test(poster) ? poster : ''
}

const artworkType = (url) => {
  const path = url.split('?')[0].toLowerCase()
  if (path.endsWith('.png')) return 'image/png'
  if (path.endsWith('.webp')) return 'image/webp'
  if (path.endsWith('.gif')) return 'image/gif'
  return 'image/jpeg'
}

const publishPlayback = (el) => {
  const title = attrText(el, 'title')
  setDocumentTitleLayer('playback', title)
  if (!('mediaSession' in navigator) || typeof MediaMetadata === 'undefined') return
  const poster = httpPoster(el)
  navigator.mediaSession.metadata = new MediaMetadata({
    title,
    artist: attrText(el, 'artist'),
    artwork: poster ? [{ src: poster, sizes: '512x512', type: artworkType(poster) }] : []
  })
}

const clearPlaybackMetadata = () => {
  clearDocumentTitleLayer('playback')
  if ('mediaSession' in navigator) navigator.mediaSession.metadata = null
}

const claimPlayback = (el, { steal = false } = {}) => {
  if (!attrText(el, 'title') || el.isProcessing) {
    releasePlayback(el)
    return
  }
  if (!registeredPlayers.includes(el)) registeredPlayers.push(el)
  if (steal || !activePlayer || activePlayer === el) {
    activePlayer = el
    publishPlayback(el)
  }
}

const releasePlayback = (el) => {
  const index = registeredPlayers.indexOf(el)
  if (index >= 0) registeredPlayers.splice(index, 1)
  if (activePlayer !== el) return
  activePlayer = registeredPlayers[registeredPlayers.length - 1] || null
  if (activePlayer) publishPlayback(activePlayer)
  else clearPlaybackMetadata()
}

class AnearVideo extends HTMLElement {
  static get observedAttributes() {
    return ['src', 'value', 'hls', 'mp4', 'status', 'poster', 'controls', 'muted', 'autoplay', 'loop', 'title', 'artist']
  }

  constructor() {
    super()
    this._videoEl = null
    this._overlayEl = null
  }

  connectedCallback() {
    this.render()
    this.syncAttributes()
    claimPlayback(this)
  }

  disconnectedCallback() {
    releasePlayback(this)
  }

  attributeChangedCallback() {
    if (this._videoEl) this.syncAttributes()
    if (this.isConnected) claimPlayback(this)
  }

  // Explicit MP4 (delivery) source, falling back to the generic src/value.
  get mp4() {
    return this.getAttribute('mp4') || this.getAttribute('src') || this.getAttribute('value') || ''
  }

  get hls() {
    return this.getAttribute('hls') || ''
  }

  get src() {
    return this.getAttribute('src') || this.getAttribute('value') || ''
  }

  get value() {
    return this.src
  }

  get poster() {
    return this.getAttribute('poster') || ''
  }

  // Generic transcode status. Anything other than "processing" is treated as ready.
  get status() {
    return (this.getAttribute('status') || '').toLowerCase()
  }

  get isProcessing() {
    return this.status === 'processing'
  }

  hasFlag(name) {
    if (!this.hasAttribute(name)) return false
    const raw = this.getAttribute(name)
    return raw !== 'false'
  }

  canPlayHls() {
    return !!this._videoEl && !!this._videoEl.canPlayType(HLS_MIME)
  }

  // Prefer HLS where natively supported, else MP4/src.
  resolvePlaybackSrc() {
    if (this.hls && this.canPlayHls()) return this.hls
    return this.mp4
  }

  syncAttributes() {
    if (!this._videoEl) return

    const poster = this.poster
    if (poster) {
      this._videoEl.setAttribute('poster', poster)
    } else {
      this._videoEl.removeAttribute('poster')
    }

    if (this.isProcessing) {
      // Show poster only; don't load a source that isn't ready yet.
      this._videoEl.removeAttribute('src')
      this._videoEl.controls = false
      this._videoEl.removeAttribute('autoplay')
      this.showOverlay(true)
      this.classList.add('is-processing')
      this.classList.remove('is-ready')
      return
    }

    this.showOverlay(false)
    this.classList.remove('is-processing')
    this.classList.add('is-ready')

    const src = this.resolvePlaybackSrc()
    if (src) {
      if (this._videoEl.getAttribute('src') !== src) this._videoEl.setAttribute('src', src)
    } else {
      this._videoEl.removeAttribute('src')
    }

    // controls default ON unless explicitly controls="false"
    const showControls = !this.hasAttribute('controls') ? true : this.getAttribute('controls') !== 'false'
    this._videoEl.controls = showControls

    this._videoEl.muted = this.hasFlag('muted')
    this._videoEl.loop = this.hasFlag('loop')
    if (this.hasFlag('autoplay')) {
      this._videoEl.setAttribute('autoplay', '')
      // Autoplay typically requires muted on mobile.
      this._videoEl.muted = true
    } else {
      this._videoEl.removeAttribute('autoplay')
    }
  }

  showOverlay(show) {
    if (!this._overlayEl) return
    this._overlayEl.style.display = show ? 'flex' : 'none'
  }

  render() {
    if (this._videoEl) return

    this.style.display = 'block'
    this.style.width = '100%'

    const wrapper = document.createElement('div')
    wrapper.className = 'anear-video'
    wrapper.style.position = 'relative'
    wrapper.style.width = '100%'

    this._videoEl = document.createElement('video')
    this._videoEl.className = 'anear-video-player'
    this._videoEl.setAttribute('playsinline', '')
    this._videoEl.playsInline = true
    this._videoEl.preload = 'metadata'
    this._videoEl.addEventListener('play', () => claimPlayback(this, { steal: true }))
    this._videoEl.style.display = 'block'
    this._videoEl.style.width = '100%'
    this._videoEl.style.height = 'auto'
    this._videoEl.style.maxWidth = '100%'
    this._videoEl.style.maxHeight = '100%'
    this._videoEl.style.objectFit = 'contain'
    this._videoEl.style.objectPosition = 'center'

    this._overlayEl = document.createElement('div')
    this._overlayEl.className = 'anear-video-processing'
    this._overlayEl.style.display = 'none'
    this._overlayEl.style.position = 'absolute'
    this._overlayEl.style.inset = '0'
    this._overlayEl.style.alignItems = 'center'
    this._overlayEl.style.justifyContent = 'center'
    this._overlayEl.style.color = '#fff'
    this._overlayEl.style.background = 'rgba(0, 0, 0, 0.35)'
    this._overlayEl.style.font = '600 0.9rem/1.2 system-ui, sans-serif'
    this._overlayEl.textContent = 'Processing…'

    wrapper.appendChild(this._videoEl)
    wrapper.appendChild(this._overlayEl)
    this.appendChild(wrapper)
  }
}

if (!customElements.get('anear-video')) {
  customElements.define('anear-video', AnearVideo)
}

export default AnearVideo
